import { useEffect, useState } from "react";
// Relative import (not "@/") so the Jest unit test can resolve it too.
import { api } from "../lib/api";

/**
 * Feature flags served by the backend at GET /api/config.
 *
 *   referralsEnabled  mirrors the REFERRALS_ENABLED env var on the backend
 *                     (Railway). Controls every referral / Ambassador / W-9 /
 *                     tax-form surface. Flipping it on the backend + redeploy
 *                     is enough; no frontend rebuild needed.
 *
 * Fails closed: while /api/config is loading, or if it fails, every flag is
 * OFF (hidden). The response is fetched once per app session and cached.
 *
 * Build-time kill switch: REACT_APP_REFERRALS_ENABLED=false forces referrals
 * OFF in that build regardless of the backend. Any other value (or unset)
 * defers to the backend.
 */
const BUILD_FORCE_REFERRALS_OFF =
  String(process.env.REACT_APP_REFERRALS_ENABLED || "").trim().toLowerCase() === "false";

const DEFAULT_FLAGS = Object.freeze({ referralsEnabled: false });

let cachedFlags = null;
let inflight = null;

function normalize(data) {
  return {
    referralsEnabled: !BUILD_FORCE_REFERRALS_OFF && data?.referrals_enabled === true,
  };
}

export function loadFeatureFlags() {
  if (cachedFlags) return Promise.resolve(cachedFlags);
  if (!inflight) {
    inflight = api
      .get("/config")
      .then((r) => {
        cachedFlags = normalize(r?.data);
        return cachedFlags;
      })
      .catch(() => {
        inflight = null; // allow a retry on the next mount; stay OFF meanwhile
        return DEFAULT_FLAGS;
      });
  }
  return inflight;
}

export function useFeatureFlags() {
  const [flags, setFlags] = useState(cachedFlags || DEFAULT_FLAGS);
  const [loaded, setLoaded] = useState(Boolean(cachedFlags));

  useEffect(() => {
    let alive = true;
    loadFeatureFlags().then((f) => {
      if (!alive) return;
      setFlags(f);
      setLoaded(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  return { ...flags, loaded };
}
